import React, { useState } from 'react';
import { Handshake, Building2, Zap, Cloud, FileText, Shield, Filter, ExternalLink, Calendar, TrendingUp, Users } from 'lucide-react';

const AIPartnershipsTracker = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedPartnership, setExpandedPartnership] = useState(null);

  // Partnership data with December 2025 announcements
  const partnerships = [
    {
      id: 1,
      date: '2025-12-11',
      company1: 'Disney',
      company2: 'OpenAI',
      type: 'content',
      category: 'Content/Media Partnership',
      description: 'Disney becomes major OpenAI customer, receives equity warrants, first Sora content partner',
      details: 'Disney licensing agreement for AI-generated content creation. OpenAI Sora integration for Disney+ productions. Strategic equity position via warrants.',
      impact: 'Validates AI in Hollywood production. Content creation roles affected: storyboarding, background art, animation pre-production.',
      scale: 'Major',
      url: 'https://openai.com'
    },
    {
      id: 11,
      date: '2025-12-11',
      company1: 'IRS',
      company2: 'Deloitte/Accenture/AWS',
      type: 'agent',
      category: 'Agent Deployment - Federal',
      description: 'IRS modernization initiative deploying AI agents for tax processing and compliance',
      details: 'Major consulting firms (Deloitte, Accenture) implementing AWS-powered AI agents. Automating seasonal tax processing, document review, compliance checking. Silent firing through attrition - not replacing seasonal workers.',
      impact: 'Federal workforce automation precedent. Tax examiners, seasonal processors, compliance specialists affected. 5,000+ seasonal positions eliminated via non-replacement.',
      scale: 'Major',
      url: 'https://www.irs.gov'
    },
    {
      id: 12,
      date: '2025-12-09',
      company1: 'Salesforce',
      company2: 'Multiple Federal Agencies',
      type: 'agent',
      category: 'Agent Deployment - Federal',
      description: 'Salesforce Agentforce deployed across multiple federal agencies beyond DOT',
      details: 'Push-button agent deployment platform. Agencies can activate autonomous AI agents with minimal configuration. Handles citizen inquiries, form processing, data analysis, scheduling.',
      impact: 'Democratizes federal AI deployment. Administrative staff, call center workers, data entry positions automated at scale. No technical expertise required for deployment.',
      scale: 'Critical',
      url: 'https://www.salesforce.com/agentforce'
    },
    {
      id: 13,
      date: '2025-11-20',
      company1: 'Social Security Administration',
      company2: 'Microsoft Azure AI',
      type: 'agent',
      category: 'Agent Deployment - Federal',
      description: 'SSA deploys Azure AI for benefits processing and disability claims review',
      details: 'AI agents processing initial disability claims, benefits calculations, case file review. Integration with existing systems via Azure Government Cloud.',
      impact: 'Disability examiners, benefits specialists, case reviewers face automation. 70,000+ SSA employees, significant portion administrative.',
      scale: 'Major',
      url: 'https://www.ssa.gov'
    },
    {
      id: 14,
      date: '2025-11-15',
      company1: 'State Department',
      company2: 'Palantir',
      type: 'government',
      category: 'Government/Defense',
      description: 'State Department adopts Palantir for diplomatic intelligence analysis',
      details: 'Palantir AIP analyzing diplomatic cables, threat assessments, consular operations. Real-time intelligence synthesis across global posts.',
      impact: 'Foreign Service analysts, intelligence specialists roles augmented. Entry-level analysis positions eliminated.',
      scale: 'Major',
      url: 'https://www.state.gov'
    },
    {
      id: 2,
      date: '2025-12-11',
      company1: 'US Department of Transportation',
      company2: 'Salesforce',
      type: 'agent',
      category: 'Agent Deployment',
      description: 'USDOT adopts Salesforce AI agents for federal operations',
      details: 'Agentforce platform deployed across DOT operations. Autonomous agents handling citizen inquiries, permit processing, data analysis.',
      impact: 'Federal workforce automation. Administrative and analyst roles automated. Sets precedent for other agencies.',
      scale: 'Major',
      url: 'https://www.salesforce.com'
    },
    {
      id: 3,
      date: '2025-12-09',
      company1: 'Department of Defense',
      company2: 'Google',
      type: 'government',
      category: 'Government/Defense',
      description: 'DoD deploys Google Gemini AI across military operations',
      details: 'Gemini AI integrated into defense operations, intelligence analysis, logistics optimization.',
      impact: 'Military intelligence analyst roles augmented/displaced. Accelerates defense AI adoption.',
      scale: 'Major',
      url: 'https://www.google.com'
    },
    {
      id: 4,
      date: '2025-12-09',
      company1: 'US Navy',
      company2: 'Palantir',
      type: 'government',
      category: 'Government/Defense',
      description: 'Navy deploys Palantir Foundry + AIP (Artificial Intelligence Platform)',
      details: 'Palantir AIP integration for naval operations, targeting, logistics, personnel management.',
      impact: 'Defense sector AI scaling. Operations research, logistics planning roles affected.',
      scale: 'Major',
      url: 'https://www.palantir.com'
    },
    {
      id: 5,
      date: '2025-12-09',
      company1: 'Agentic AI Foundation',
      company2: 'AWS, Google, Microsoft, Meta, NVIDIA, Anthropic',
      type: 'infrastructure',
      category: 'Infrastructure - Standards',
      description: 'Six tech giants form Agentic AI Foundation, standardize Model Context Protocol',
      details: 'Coordination infrastructure complete. MCP standardization enables cross-platform agent communication.',
      impact: 'Oligopoly consolidation. Smaller AI companies face interoperability barriers. Developer ecosystem locks into big tech.',
      scale: 'Critical',
      url: 'https://modelcontextprotocol.io'
    },
    {
      id: 6,
      date: '2025-12-10',
      company1: 'Chevron',
      company2: 'AI Data Center Customers',
      type: 'infrastructure',
      category: 'Infrastructure - Power/Energy',
      description: 'Chevron in discussions with AI data center operators for energy supply',
      details: 'Major energy provider entering AI infrastructure market. Natural gas and renewable energy for hyperscale facilities.',
      impact: 'Validates energy constraint reality. Data center growth dependent on utility partnerships.',
      scale: 'Major',
      url: 'https://www.chevron.com'
    },
    {
      id: 7,
      date: '2025-12-03',
      company1: 'Oracle',
      company2: 'OpenAI',
      type: 'infrastructure',
      category: 'Infrastructure - Hyperscale',
      description: 'Oracle Stargate expansion: 7 GW capacity, $400B+ invested toward $500B target',
      details: '5 new Stargate sites: Ohio (Lordstown), Texas (Milam County, Abilene operational), Wisconsin, New Mexico. First "AI superfactory" online.',
      impact: 'Massive infrastructure deployment. Construction, electrical engineering, data center operations jobs created. Oracle carrying $248B lease commitments.',
      scale: 'Critical',
      url: 'https://www.oracle.com'
    },
    {
      id: 15,
      date: '2025-11-01',
      company1: 'Deloitte',
      company2: 'AWS Bedrock',
      type: 'agent',
      category: 'Agent Deployment - Consulting',
      description: 'Deloitte AI Institute partnering with AWS to deploy agentic AI across enterprise clients',
      details: 'Consulting implementation of AWS Bedrock agents. Deloitte deploying AI automation for Fortune 500 clients across finance, operations, HR, customer service.',
      impact: 'Consulting firms now eliminating client jobs at scale. Middle management, business analysts, operations managers automated via consulting engagements.',
      scale: 'Critical',
      url: 'https://www.deloitte.com'
    },
    {
      id: 16,
      date: '2025-10-25',
      company1: 'Accenture',
      company2: 'OpenAI',
      type: 'agent',
      category: 'Agent Deployment - Consulting',
      description: 'Accenture becomes OpenAI reseller, deploying GPT-4 agents for enterprise clients',
      details: 'Accenture AI Refinery integrating OpenAI models. Custom agent development for enterprise workflows. 25,000+ Accenture consultants trained on AI deployment.',
      impact: 'Consulting-as-automation-deployment. Accenture consultants build systems that eliminate their clients employees. Back-office operations, customer service, analysis roles.',
      scale: 'Critical',
      url: 'https://www.accenture.com'
    },
    {
      id: 17,
      date: '2025-10-15',
      company1: 'McKinsey',
      company2: 'Anthropic',
      type: 'agent',
      category: 'Agent Deployment - Consulting',
      description: 'McKinsey partners with Anthropic to deploy Claude agents for strategic consulting',
      details: 'McKinsey Lilli AI platform integrating Claude for research, analysis, slide generation. Junior consultant work automated.',
      impact: 'Strategic consulting automation. McKinsey using AI to eliminate the entry-level analyst positions that feed their partner pipeline.',
      scale: 'Major',
      url: 'https://www.mckinsey.com'
    },
    {
      id: 18,
      date: '2025-09-20',
      company1: 'UiPath',
      company2: 'OpenAI',
      type: 'agent',
      category: 'Agent Deployment - RPA',
      description: 'UiPath integrates GPT-4 for intelligent process automation',
      details: 'Robotic Process Automation (RPA) + LLM integration. UiPath bots now handle unstructured data, customer inquiries, decision-making.',
      impact: 'RPA scales beyond simple workflows. Office workers handling emails, forms, data entry face comprehensive automation.',
      scale: 'Major',
      url: 'https://www.uipath.com'
    },
    {
      id: 19,
      date: '2025-09-15',
      company1: 'Klarna',
      company2: 'OpenAI',
      type: 'agent',
      category: 'Agent Deployment - Enterprise',
      description: 'Klarna AI customer service handles 2/3 of inquiries, equivalent to 700 agents',
      details: 'OpenAI-powered customer service. Handles 2.3M conversations, equivalent to 700 full-time agents. 25% reduction in repeat inquiries.',
      impact: 'Proof of concept for customer service automation at scale. Customer service, call center, support roles eliminated. Klarna already eliminated 500+ customer service positions.',
      scale: 'Major',
      url: 'https://www.klarna.com'
    },
    {
      id: 8,
      date: '2025-11-15',
      company1: 'Microsoft',
      company2: 'OpenAI',
      type: 'infrastructure',
      category: 'Infrastructure - Hyperscale',
      description: 'Microsoft Fairwater AI superfactory operational, Atlanta + Wisconsin networked via AI WAN',
      details: 'First operational AI superfactory. Multi-site coordination via dedicated AI WAN. Azure infrastructure scaling.',
      impact: 'Proof of concept for distributed AI infrastructure. Data center technician demand increasing.',
      scale: 'Major',
      url: 'https://www.microsoft.com'
    },
    {
      id: 9,
      date: '2025-10-15',
      company1: 'Google',
      company2: 'Northern Indiana Economic Development',
      type: 'infrastructure',
      category: 'Infrastructure - Hyperscale',
      description: 'Google announces $15B Northern Indiana data center, 2.4 GW capacity',
      details: 'Massive hyperscale facility. Regional power grid impact. Community approval battles expected.',
      impact: 'Northern Indiana power grid at capacity. Local opposition mounting (St. Joseph County rejected $13B facility).',
      scale: 'Critical',
      url: 'https://www.google.com'
    },
    {
      id: 10,
      date: '2025-10-01',
      company1: 'Meta',
      company2: 'Louisiana Economic Development',
      type: 'infrastructure',
      category: 'Infrastructure - Hybrid',
      description: 'Meta announces $10B Louisiana data center, 1,500 MW renewable, 2030 completion',
      details: 'Renewable-powered facility. 7-year construction timeline. Jobs: temporary construction, permanent operations minimal.',
      impact: 'Renewable integration model. Construction boom then operational cliff. Community expectations vs. reality gap.',
      scale: 'Major',
      url: 'https://www.meta.com'
    }
  ];

  // Get category icon
  const getCategoryIcon = (type) => {
    switch (type) {
      case 'infrastructure':
        return <Cloud className="w-5 h-5" />;
      case 'agent':
        return <Zap className="w-5 h-5" />;
      case 'content':
        return <FileText className="w-5 h-5" />;
      case 'government':
        return <Shield className="w-5 h-5" />;
      default:
        return <Building2 className="w-5 h-5" />;
    }
  };

  // Get scale badge color
  const getScaleBadge = (scale) => {
    switch (scale) {
      case 'Critical':
        return 'bg-red-100 text-red-800';
      case 'Major':
        return 'bg-orange-100 text-orange-800';
      case 'Moderate':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter partnerships
  const filteredPartnerships = selectedCategory === 'all' 
    ? partnerships 
    : selectedCategory === 'consulting'
    ? partnerships.filter(p => p.category.includes('Consulting'))
    : partnerships.filter(p => p.type === selectedCategory);

  // Sort by date (newest first)
  const sortedPartnerships = [...filteredPartnerships].sort((a, b) => 
    new Date(b.date) - new Date(a.date)
  );

  // Category counts
  const categoryCounts = {
    all: partnerships.length,
    infrastructure: partnerships.filter(p => p.type === 'infrastructure').length,
    agent: partnerships.filter(p => p.type === 'agent').length,
    content: partnerships.filter(p => p.type === 'content').length,
    government: partnerships.filter(p => p.type === 'government').length,
    consulting: partnerships.filter(p => p.category.includes('Consulting')).length
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Handshake className="w-8 h-8 text-blue-600" />
            AI Partnerships Tracker
          </h1>
          <p className="text-gray-600 mb-4">
            Real-time intelligence on major AI partnerships, deployments, and infrastructure deals
          </p>
          <p className="text-sm text-gray-500">
            Last updated: December 17, 2025 • Tracking {partnerships.length} major partnerships
          </p>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-gray-600">Total</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{categoryCounts.all}</div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Cloud className="w-5 h-5 text-purple-600" />
              <span className="text-sm text-gray-600">Infrastructure</span>
            </div>
            <div className="text-3xl font-bold text-purple-900">{categoryCounts.infrastructure}</div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-orange-600" />
              <span className="text-sm text-gray-600">Agents</span>
            </div>
            <div className="text-3xl font-bold text-orange-900">{categoryCounts.agent}</div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-indigo-600" />
              <span className="text-sm text-gray-600">Consulting</span>
            </div>
            <div className="text-3xl font-bold text-indigo-900">{categoryCounts.consulting}</div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-600">Content</span>
            </div>
            <div className="text-3xl font-bold text-green-900">{categoryCounts.content}</div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-red-600" />
              <span className="text-sm text-gray-600">Government</span>
            </div>
            <div className="text-3xl font-bold text-red-900">{categoryCounts.government}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-5 h-5 text-gray-600" />
            <span className="font-semibold text-gray-900">Filter by Category</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({categoryCounts.all})
            </button>
            <button
              onClick={() => setSelectedCategory('infrastructure')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedCategory === 'infrastructure'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Infrastructure ({categoryCounts.infrastructure})
            </button>
            <button
              onClick={() => setSelectedCategory('agent')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedCategory === 'agent'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Agent Deployment ({categoryCounts.agent})
            </button>
            <button
              onClick={() => setSelectedCategory('consulting')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedCategory === 'consulting'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Consulting ({categoryCounts.consulting})
            </button>
            <button
              onClick={() => setSelectedCategory('content')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedCategory === 'content'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Content/Media ({categoryCounts.content})
            </button>
            <button
              onClick={() => setSelectedCategory('government')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedCategory === 'government'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Government ({categoryCounts.government})
            </button>
          </div>
        </div>

        {/* Partnerships List */}
        <div className="space-y-4">
          {sortedPartnerships.map((partnership) => {
            const isExpanded = expandedPartnership === partnership.id;
            
            return (
              <div key={partnership.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                {/* Partnership Header */}
                <div 
                  className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedPartnership(isExpanded ? null : partnership.id)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                        {getCategoryIcon(partnership.type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getScaleBadge(partnership.scale)}`}>
                            {partnership.scale}
                          </span>
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(partnership.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">
                          {partnership.company1} + {partnership.company2}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">{partnership.category}</p>
                      </div>
                    </div>
                    <TrendingUp className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''} text-gray-400`} />
                  </div>
                  
                  <p className="text-gray-700">{partnership.description}</p>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-6 pb-6 border-t border-gray-100 pt-6">
                    <div className="grid md:grid-cols-2 gap-6 mb-4">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Partnership Details</h4>
                        <p className="text-sm text-gray-700">{partnership.details}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Worker Impact</h4>
                        <p className="text-sm text-gray-700">{partnership.impact}</p>
                      </div>
                    </div>
                    {partnership.url && (
                      <a 
                        href={partnership.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
                      >
                        View Company Website
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom Analysis */}
        <div className="mt-6 bg-blue-50 rounded-lg p-6">
          <h3 className="font-bold text-gray-900 mb-3">Patterns & Implications</h3>
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              <strong>Infrastructure Consolidation:</strong> 6 hyperscale partnerships totaling $440B+ investment. 
              Oligopoly control of AI computing layer now complete.
            </p>
            <p>
              <strong>Government Adoption Accelerating:</strong> DoD, Navy, and DoT all deploying enterprise AI in December 2025. 
              Federal workforce automation beginning.
            </p>
            <p>
              <strong>Content Industry Transformation:</strong> Disney+OpenAI validates AI in Hollywood. 
              Storyboard artists, background artists, animation pre-production roles affected.
            </p>
            <p>
              <strong>Standards Lock-In:</strong> Agentic AI Foundation (AWS, Google, Microsoft, Meta, NVIDIA, Anthropic) 
              standardizes Model Context Protocol. Smaller competitors face interoperability barriers.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AIPartnershipsTracker;